import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { dropTask } from 'ember-concurrency';
import RouteTemplate from 'ember-route-template';
import set from 'ember-set-helper/helpers/set';
import { and, gt } from 'ember-truth-helpers';
import RolesCreateProductPermissions from 'garaje/components/roles/create-product-permissions';
import RolesCreateRoleGridHeader from 'garaje/components/roles/create-role/grid-header';
import RolesEnterpriseDialog from 'garaje/components/roles/enterprise-dialog';
import RolesRequestSentDialog from 'garaje/components/roles/request-sent-dialog';
import ButtonGroup from 'garaje/components/ui/button-group';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiInputField from 'garaje/components/ui-input-field';
import UiLabel from 'garaje/components/ui-label';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { adminRolesRoute } from 'garaje/utils/admin-routing';
import { getRoleIcon, RoleId } from 'garaje/utils/custom-roles';
import { MatrixKind, displayDescriptionKeyMapping, displayNameKeyMapping } from 'garaje/utils/permission-sets';
import type { MatrixByProduct, MatrixItem, MatrixRow } from 'garaje/utils/permission-sets';

import type { CreateRoleModel } from './route';

interface MatrixByGrid {
  grid: MatrixRow;
  products: Array<MatrixByProduct>;
  gridIndex: number;
}

interface ManageCreateRoleTemplateSignature {
  Args: {
    model: CreateRoleModel;
  };
}

class ManageCreateRoleTemplate extends Component<ManageCreateRoleTemplateSignature> {
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare router: RouterService;
  @service declare flashMessages: FlashMessagesService;

  @tracked name: string = '';
  @tracked description: string = '';
  @tracked permissionSetIds: Array<string> = this.selectedPermissionSets;
  @tracked showUpsellModal = false;
  @tracked showRequestSentModal = false;

  constructor(owner: unknown, args: ManageCreateRoleTemplateSignature['Args']) {
    super(owner, args);
    // if editing a role, pre populate role name + description
    if (this.isEditing) {
      this.name = this.args.model.role.name;
      this.description = this.args.model.role.description;
    }

    this.permissionSetIds = this.selectedPermissionSets;
  }

  willDestroy(): void {
    super.willDestroy();
    this.name = '';
    this.description = '';
    this.permissionSetIds = [];
  }

  get isEditing(): boolean {
    return (
      this.router.currentRouteName !== 'manage.roles' &&
      this.router.currentRouteName !== 'location-overview.manage.roles' &&
      !this.isCreate
    );
  }

  get iconPath(): string {
    const { role } = this.args.model;
    return role ? getRoleIcon(role.id) : '';
  }

  get isCreate(): boolean {
    return this.router.currentURL.includes('create');
  }

  get routeBackTo(): string {
    return adminRolesRoute(this.router.currentRouteName, 'manage.roles');
  }

  get selectedPermissionSets(): Array<string> {
    const { matrix } = this.args.model;
    const selectedIds: Array<string> = [];

    if (matrix && matrix.rows?.length > 0) {
      matrix.rows.forEach((row) => {
        if (row.kind === MatrixKind.PERMISSION_SET && row.item && row.selected) {
          selectedIds.push(row.item.id);
        }
      });
    }

    return selectedIds;
  }

  get isNotStartFromScratch(): boolean {
    const { role } = this.args.model;
    return role?.id !== RoleId.COMPANY_START_FROM_SCRATCH && role?.id !== RoleId.LOCATION_START_FROM_SCRATCH;
  }

  get copiedFrom(): string | null {
    return this.isNotStartFromScratch ? this.args.model.role.name : null;
  }

  // break down the matrix into a usable set of grids > products > categories > permission sets
  get matrixByGrids(): Array<MatrixByGrid> {
    const { matrix } = this.args.model;

    const matrixByGrids: Array<MatrixByGrid> = [];
    let currentGrid: MatrixByGrid;
    let products: Array<MatrixByProduct> = [];
    let currentProduct: MatrixByProduct;

    if (matrix && matrix.rows?.length > 0) {
      matrix.rows.forEach((row: MatrixRow, i: number) => {
        switch (row.kind) {
          case MatrixKind.GRID:
            if (currentGrid && Object.keys(currentGrid).length > 0) {
              // new grid, add everything to currentGrid
              products.push(currentProduct);
              currentGrid.products = products;
              matrixByGrids.push(currentGrid);
            }
            // reset
            currentGrid = {
              grid: row,
              products: [],
              gridIndex: i === 0 ? 0 : currentGrid.gridIndex + 1,
            };
            products = [];
            currentProduct = {
              product: undefined,
              rows: [],
            };
            break;
          case MatrixKind.PRODUCT:
            if (currentProduct && Object.keys(currentProduct).length > 0 && currentProduct.product) {
              // new product, add everything to currentProduct
              products.push(currentProduct);
            }
            // reset
            currentProduct = {
              product: row,
              rows: [],
            };
            break;
          case MatrixKind.CATEGORY:
          case MatrixKind.PERMISSION_SET:
          default:
            currentProduct.rows.push(row);
            if (i === matrix.rows.length - 1) {
              // last item in the matrix, push to matrixByGrids
              products.push(currentProduct);
              currentGrid.products = products;
              matrixByGrids.push(currentGrid);
            }
        }
      });
    }

    return matrixByGrids;
  }

  get permissionSetsById(): Record<string, MatrixItem> {
    return this.args.model.matrix.rows.reduce<Record<string, MatrixItem>>((acc: Record<string, MatrixItem>, row) => {
      if (row.kind === MatrixKind.PERMISSION_SET && row.item) {
        acc[row.item.id] = row.item;
      }
      return acc;
    }, {});
  }

  renderGridName(row: MatrixRow): string {
    return displayNameKeyMapping[row.displayNameKey] || '';
  }

  get renderGridDescription(): (row: MatrixRow) => string {
    return (row: MatrixRow) => {
      if (
        row.displayDescriptionKey === 'grid.workplace.description' &&
        !this.featureFlags.isEnabled('wrkx-pricing-packaging')
      ) {
        return displayDescriptionKeyMapping['grid.workplace.description.legacy'] || '';
      }
      return displayDescriptionKeyMapping[row.displayDescriptionKey] || '';
    };
  }

  get upsellModalDescription(): (row: MatrixRow) => string {
    return (row: MatrixRow) => {
      let product: string;
      let sku: string;
      let upsellTier: string;

      if (row.displayNameKey === 'grid.visitors.name') {
        product = 'Visitors';
        sku = 'Visitors';
        upsellTier = 'Enterprise';
      } else {
        product = 'Workplace';
        if (!this.featureFlags.isEnabled('wrkx-pricing-packaging')) {
          sku = 'Workplace';
          upsellTier = 'Premium Plus';
        } else {
          sku = 'Platform';
          upsellTier = 'Enterprise';
        }
      }

      return `
        Control viewing and editing permissions for ${product} features. To unlock custom admin roles for ${product},
        please contact us to upgrade to our ${sku} ${upsellTier} plan.
      `;
    };
  }

  @action
  toggleUpsellModal(): void {
    this.showUpsellModal = !this.showUpsellModal;
  }

  @action
  addPermission(item: MatrixItem): void {
    const permissionSetId: string = item.id;
    const permissionsToAdd: Array<string> = [permissionSetId];
    if (item.dependsOn && item.dependsOn.length > 0) {
      item.dependsOn.forEach((id) => {
        const permissionSet = this.permissionSetsById[id];

        if (!this.permissionSetIds.includes(id)) {
          permissionsToAdd.push(id);
        }

        // dependency has additional dependencies
        if (permissionSet?.dependsOn.length) {
          permissionSet.dependsOn.forEach((id) => {
            if (!this.permissionSetIds.includes(id)) {
              permissionsToAdd.push(id);
            }
          });
        }
      });
    }
    this.permissionSetIds = [...this.permissionSetIds, ...permissionsToAdd];
  }

  @action
  removePermission(item: MatrixItem): void {
    const permissionSetId: string = item.id;
    const permissionsToRemove: Array<string> = [permissionSetId];
    if (item.dependencyFor && item.dependencyFor.length > 0) {
      item.dependencyFor.forEach((id) => {
        const permissionSet = this.permissionSetsById[id];

        if (this.permissionSetIds.includes(id)) {
          permissionsToRemove.push(id);
        }

        // dependency has additional dependencies
        if (permissionSet?.dependencyFor.length) {
          permissionSet.dependencyFor.forEach((id) => {
            if (this.permissionSetIds.includes(id)) {
              permissionsToRemove.push(id);
            }
          });
        }
      });
    }

    this.permissionSetIds = this.permissionSetIds.filter((id) => !permissionsToRemove.includes(id));
  }

  @action
  displayRequestSentModal(): void {
    this.showUpsellModal = false;
    this.showRequestSentModal = true;
    // eslint-disable-next-line ember/no-runloop
    later(() => {
      this.showRequestSentModal = false;
    }, 2500);
  }

  createRoleTask = dropTask(async () => {
    if (!this.#validateRole()) return;

    try {
      const permissionSets = this.permissionSetIds.map((id) => {
        return { 'permission-set-id': id, effect: 'allow' };
      });
      await this.authz.createRole({
        name: this.name,
        description: this.description,
        'role-scope': this.args.model.role.roleScope,
        'legacy-role': this.args.model.role.legacyRole,
        'copied-from': this.copiedFrom,
        'permission-sets': permissionSets,
      });
      this.flashMessages.showAndHideFlash('success', 'Admin role created');
      void this.router.transitionTo(this.routeBackTo);
    } catch (e) {
      let message = 'Something went wrong, please try again.';
      const error = <{ payload: Array<{ detail: string }> }>e;

      if (error?.payload[0]?.detail) message = error.payload[0].detail;

      console.error(e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', message);
    }
  });

  updateRoleTask = dropTask(async () => {
    if (!this.#validateRole()) return;

    try {
      const permissionSets = this.permissionSetIds.map((id) => {
        return { 'permission-set-id': id, effect: 'allow' };
      });
      await this.authz.updateRole(
        {
          name: this.name,
          description: this.description,
          'permission-sets': permissionSets,
        },
        this.args.model.role.id,
      );
      this.flashMessages.showAndHideFlash('success', 'Admin role updated');
      void this.router.transitionTo(this.routeBackTo);
    } catch (e) {
      let message = 'Something went wrong, please try again.';
      const error = <{ payload: Array<{ detail: string }> }>e;

      if (error?.payload[0]?.detail) message = error.payload[0].detail;

      console.error(e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', message);
    }
  });

  #validateRole(): boolean {
    if (isBlank(this.name)) {
      this.flashMessages.showAndHideFlash('error', 'Role name is required');
      return false;
    } else if (isBlank(this.description)) {
      this.flashMessages.showAndHideFlash('error', 'Role description is required');
      return false;
    }
    return true;
  }

  <template>
    <UiPage class="min-w-[1000px]">
      <div class="flex w-full">
        <div class="flex items-center w-1/2">
          <UiHeader>{{if this.isCreate "New" "Edit"}} {{@model.role.roleScope}} role</UiHeader>
        </div>
        <div class="flex-1 w-1/2">
          <div class="float-right">
            <ButtonGroup>
              <LinkTo @route={{this.routeBackTo}} class="btn btn--outline">
                Cancel
              </LinkTo>
              <button
                class="btn btn--brand"
                {{on "click" (if this.isCreate this.createRoleTask.perform this.updateRoleTask.perform)}}
              >
                {{if this.isCreate "Create" "Update"}}
              </button>
            </ButtonGroup>
          </div>
        </div>
      </div>

      <div class="p-8 border-2 border-carbon-10 rounded-lg w-full">
        <div class="flex mb-10">
          <UiHeader>Role info</UiHeader>
        </div>

        <div class="grid grid-cols-3 space-x-10">
          <div class="flex-1 col-span-2">
            <div class="flex-row">
              <UiInputField
                @required={{true}}
                @placeholder="e.g. Desks Manager or Front Desk Admin - No data access"
                class="flex-auto w-full min-w-0 min-h-0 items-center mb-5"
                as |Field|
              >
                <Field.label>Role name</Field.label>
                <Field.input
                  class="!mb-1"
                  @value={{this.name}}
                  @update={{set this "name"}}
                  @autofocus={{false}}
                  maxlength="40"
                />
              </UiInputField>

              <UiLabel @required={{true}} @for="role-description-textarea" @text="Role description" />
              <textarea
                value={{this.description}}
                id="role-description-textarea"
                required={{true}}
                placeholder="Enter a description for your role"
                rows="6"
                autocomplete="off"
                class="resize-none"
                {{on "change" (pick "target.value" (set this "description"))}}
              />
            </div>
          </div>

          <div class="flex col-span-1">
            <div class="flex-row">
              <p>
                <span class="text-sm text-carbon-40">Role type</span><br />
                <span class="capitalize">{{@model.role.roleScope}}</span>
                role
              </p>
              {{#if (and this.isCreate this.isNotStartFromScratch)}}
                <p>
                  <span class="text-sm text-carbon-40">Copied from</span><br />
                  {{@model.role.name}}
                </p>
              {{/if}}
            </div>
          </div>
        </div>
      </div>

      <div class="p-8 border-2 border-carbon-10 rounded-lg w-full">
        {{#each this.matrixByGrids as |gridMatrix|}}
          <RolesCreateRoleGridHeader
            @gridName={{this.renderGridName gridMatrix.grid}}
            @gridDescription={{this.renderGridDescription gridMatrix.grid}}
            @gridIndex={{gridMatrix.gridIndex}}
            @toggleUpsellModal={{this.toggleUpsellModal}}
          />
          {{#each gridMatrix.products as |productMatrix|}}
            <RolesCreateProductPermissions
              @productMatrix={{productMatrix}}
              @permissionSetIds={{this.permissionSetIds}}
              @addPermission={{this.addPermission}}
              @removePermission={{this.removePermission}}
              @copiedFrom={{if this.isCreate this.copiedFrom null}}
            />
          {{/each}}
          {{#if (gt gridMatrix.gridIndex 0)}}
            {{#if this.showUpsellModal}}
              <RolesEnterpriseDialog
                @closeEnterpriseDialog={{this.toggleUpsellModal}}
                @displayRequestSentDialog={{this.displayRequestSentModal}}
                @header="Experience full customization"
                @description={{this.upsellModalDescription gridMatrix.grid}}
              />
            {{/if}}
            {{#if this.showRequestSentModal}}
              <RolesRequestSentDialog @closeRequestSentDialog={{set this "showRequestSentModal" false}} />
            {{/if}}
          {{/if}}
        {{/each}}
      </div>
    </UiPage>
  </template>
}

export default RouteTemplate(ManageCreateRoleTemplate);
